import React, { useState } from 'react';
import WatchlistContractCard from '../WatchlistContractCard';
import StockHoldingCard from './StockHoldingCard';
import PortfolioSummary from './PortfolioSummary';
import BaselineToggle from '../Watchlist/BaselineToggle';
import apiClient, { refreshGroupContracts, getJobStatus } from '../../services/api';
import { isUsageLimitError, getUsageLimitData } from '../../utils/usageLimitHelper';
import UsageLimitModal from '../UsageLimitModal';
import { isStockRow } from './portfolioConstants';

/**
 * The portfolio's holdings panel.
 *
 * This is WatchlistGroups collapsed to a single group. Gone are the things a
 * singleton can't have: the groups.map(), rename, delete-group, and
 * "Assign Selected to Groups". Kept: refresh-with-progress, run, the card grid
 * and the bulk action bar.
 *
 * Unlike the watchlist it does not cap the grid at 4 cards — a portfolio is
 * what you actually own, so all of it should be visible without a click.
 */
export default function PortfolioPanel({
  portfolio,
  onRunPortfolio,
  refetchPortfolio,
  selectedContracts,
  setSelectedContracts,
  onRunSelected,
  onSimulationStart,
  onSimulationComplete,
  baselineMode,
  setBaselineMode,
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshJob, setRefreshJob] = useState(null);
  const [usageLimitData, setUsageLimitData] = useState(null);

  const contracts = portfolio?.contracts || [];
  const portfolioId = portfolio?.id;

  const handleResetDays = async (contractId) => {
    try {
      await apiClient.patch(`/saved-contracts/${contractId}/reset-days/`);
      await refetchPortfolio();
    } catch (err) {
      console.error('❌ Error resetting countdown:', err);
      alert('Failed to reset countdown.');
    }
  };

  const handleRefreshOne = async (contractId) => {
    try {
      await apiClient.patch(`/saved-contracts/${contractId}/refresh/`);
      await refetchPortfolio();
    } catch (err) {
      console.error('❌ Error refreshing position:', err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else {
        alert('Failed to refresh position.');
      }
    }
  };

  const handleRemoveOne = async (contractId) => {
    try {
      await apiClient.delete(`/watchlist-groups/${portfolioId}/contracts/${contractId}/`);
      await refetchPortfolio();
    } catch (err) {
      console.error('❌ Error removing position:', err);
      alert('Failed to remove position from portfolio.');
    }
  };

  // Recursive poll rather than an interval: each tick only schedules the next
  // once the previous response is in, so a slow backend can't stack requests.
  const pollJobStatus = async (jobId) => {
    try {
      const response = await getJobStatus(jobId);
      const job = response.data;
      setRefreshJob(job);

      if (job.status === 'completed') {
        setIsRefreshing(false);
        if (job.failed_contracts > 0) {
          alert(
            `Refresh completed: ${job.successful_contracts} updated, ${job.failed_contracts} failed.`
          );
        }
        await refetchPortfolio();
      } else if (job.status === 'failed') {
        setIsRefreshing(false);
        alert(`Refresh failed: ${job.error_message || 'Unknown error'}`);
      } else {
        setTimeout(() => pollJobStatus(jobId), 2000);
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      setIsRefreshing(false);
    }
  };

  const handleRefreshAll = async () => {
    if (!portfolioId) return;
    try {
      setIsRefreshing(true);
      const response = await refreshGroupContracts(portfolioId);
      pollJobStatus(response.data.job_id);
    } catch (err) {
      console.error('❌ Error starting portfolio refresh:', err);
      setIsRefreshing(false);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else {
        alert('Failed to start refresh.');
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedContracts.length} selected position(s)?`)) return;
    try {
      for (const contractId of selectedContracts) {
        await apiClient.delete(`/saved-contracts/${contractId}/`);
      }
      setSelectedContracts([]);
      await refetchPortfolio();
    } catch (err) {
      console.error('❌ Error deleting positions:', err);
      alert('Failed to delete positions.');
    }
  };

  const toggleSelect = (id, checked) => {
    setSelectedContracts((prev) =>
      checked ? [...prev, id] : prev.filter((cid) => cid !== id)
    );
  };

  return (
    <div data-tour="portfolio-holdings">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
        <span className="text-sm text-[color:var(--pf-muted)]">
          {contracts.length} position{contracts.length === 1 ? '' : 's'}
        </span>

        <div className="flex flex-wrap gap-2 items-center">
          <BaselineToggle mode={baselineMode} onChange={setBaselineMode} size="sm" showLabel />
          <button
            onClick={handleRefreshAll}
            disabled={isRefreshing || contracts.length === 0}
            className={`btn-secondary text-xs ${
              isRefreshing || contracts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="Fetch the latest prices, premiums and greeks for every position you hold."
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button
            onClick={() => onRunPortfolio(portfolioId)}
            disabled={isRefreshing || contracts.length === 0}
            className={`btn-primary text-xs ${
              isRefreshing || contracts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="Simulate your option positions across price scenarios. Results appear in the table below."
          >
            Run Portfolio
          </button>
        </div>
      </div>

      <PortfolioSummary contracts={contracts} />

      {isRefreshing && refreshJob && (
        <div className="pf-inset mb-3">
          <div className="text-xs text-[color:var(--pf-label)]">
            Refreshing {refreshJob.processed_contracts || 0} of {refreshJob.total_contracts || 0}
            {refreshJob.progress_percentage ? ` (${refreshJob.progress_percentage}%)` : ''}
          </div>
          <div className="w-full bg-[color:var(--pf-control)] rounded-full h-2 mt-1">
            <div
              className="bg-[color:var(--pf-accent)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${refreshJob.progress_percentage || 0}%` }}
            />
          </div>
        </div>
      )}

      {contracts.length > 0 ? (
        <div className="pf-card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...contracts]
            .sort((a, b) => a.ticker.localeCompare(b.ticker))
            .map((contract) =>
              isStockRow(contract) ? (
                <StockHoldingCard
                  key={contract.id}
                  contract={contract}
                  isSelected={selectedContracts.includes(contract.id)}
                  onSelect={toggleSelect}
                  onRefresh={handleRefreshOne}
                  onDelete={handleRemoveOne}
                  onContractUpdate={refetchPortfolio}
                />
              ) : (
                <WatchlistContractCard
                  key={contract.id}
                  contract={contract}
                  isSelected={selectedContracts.includes(contract.id)}
                  onSelect={toggleSelect}
                  onReset={handleResetDays}
                  onRefresh={handleRefreshOne}
                  onDelete={handleRemoveOne}
                  onContractUpdate={refetchPortfolio}
                  baselineMode={baselineMode}
                  setBaselineMode={setBaselineMode}
                  // No onUpdateGroups: a portfolio position has one home.
                />
              )
            )}
        </div>
      ) : (
        <div className="text-sm text-muted pl-1 italic">
          Your portfolio is empty. Add an option contract or a stock above to start tracking it.
        </div>
      )}

      {selectedContracts.length > 0 && (
        <div className="flex gap-2 mt-4 flex-wrap">
          <button
            onClick={async () => {
              onSimulationStart(selectedContracts.length);
              try {
                await onRunSelected();
              } catch (err) {
                console.error('Error running selected:', err);
                onSimulationComplete([]);
              }
            }}
            className="btn-primary"
          >
            Run Selected ({selectedContracts.length})
          </button>
          <button onClick={handleDeleteSelected} className="btn-ghost-danger">
            Delete Selected ({selectedContracts.length})
          </button>
        </div>
      )}

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}
    </div>
  );
}
